<script setup lang="ts">
import type * as monacoEditor from "monaco-editor";
import {computed, defineAsyncComponent, onMounted, shallowRef} from "vue";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import MonacoEditorUtils from "@/utils/common/MonacoEditorUtils.ts";
import FieldSubType from "@/utils/constants/FieldSubType.ts";
import NamaResizer from "@/components/NamaResizer.vue";

const VueMonacoEditor = defineAsyncComponent(() =>
  import("@guolao/vue-monaco-editor").then(module => module.VueMonacoEditor)
);

const props = withDefaults(defineProps<{
  isFieldDisabled?: boolean,
  supportResize?: boolean,
  fullSize?: boolean,
  fieldSubType?: typeof FieldSubType[keyof typeof FieldSubType]
}>(), {supportResize: true, fullSize: false, fieldSubType: "SqlQuery"})

const emit = defineEmits<{ (e: "onEditorLoaded", editor: monacoEditor.editor.IStandaloneCodeEditor): void }>()
const {darkMode} = storeToRefs(useNamaFlagsStore())
const editor = shallowRef<monacoEditor.editor.IStandaloneCodeEditor>();
const editorCode = defineModel({type: String, default: ""})
const MONACO_EDITOR_OPTIONS: monacoEditor.editor.IStandaloneEditorConstructionOptions = {
  automaticLayout: true,
  formatOnType: true,
  formatOnPaste: true,
  minimap: {enabled: false},
  fontSize: 14,
  lineNumbers: 'on',
  scrollBeyondLastLine: false,
  wordWrap: 'on',
  tabSize: 2,
  insertSpaces: true
}

const editorTheme = computed(() => {
  if (props.fieldSubType === FieldSubType.TempoTemplate)
    return darkMode.value ? "tempo-dark" : "tempo-light"
  else
    return darkMode.value ? "vs-dark" : "hc-light"
})

function addEditorLanguage() {
  switch (props.fieldSubType) {
    case FieldSubType.SqlQuery:
      return "sql"
    case FieldSubType.TempoTemplate:
      return "tempo";
    case FieldSubType.Xml:
      return "xml";
    case "Json":
      return "json";
  }
}

const handleMount = (editorInstance: monacoEditor.editor.IStandaloneCodeEditor) => {
  editor.value = editorInstance;
  emit("onEditorLoaded", editorInstance)
};

async function addSuggestions() {
  switch (props.fieldSubType) {
    case FieldSubType.SqlQuery:
      return await MonacoEditorUtils.addSQLSuggestions()
    case FieldSubType.TempoTemplate:
      return await MonacoEditorUtils.addTempoSuggestions();
  }
}

onMounted(async () => {
  await addSuggestions();
})

defineExpose({editor})
</script>

<template>
  <div
    v-if="fullSize"
    class="tw-w-full tw-h-full tw-relative tw-rounded-lg tw-overflow-hidden tw-border-2 tw-border-gray-300 dark:tw-border-gray-600">
    <VueMonacoEditor
      v-model:value="editorCode"
      :options="MONACO_EDITOR_OPTIONS"
      :theme="editorTheme"
      @mount="handleMount"
      :language="addEditorLanguage()"
      v-bind="{disabled:isFieldDisabled||undefined}"/>
  </div>
  <NamaResizer v-else :supportResize="supportResize" #="{isResizing}" :defaultHeight="180">
    <div
      class="tw-w-full tw-relative tw-rounded-lg tw-overflow-hidden tw-border-2 tw-h-full tw-border-gray-300 dark:tw-border-gray-600 tw-transition-colors"
      :class="{'tw-border-primary-light dark:tw-border-primary-dark': isResizing}">
      <VueMonacoEditor
        v-model:value="editorCode"
        :options="MONACO_EDITOR_OPTIONS"
        :theme="editorTheme"
        @mount="handleMount"
        :language="addEditorLanguage()"
        v-bind="{disabled:isFieldDisabled||undefined}"/>
    </div>
  </NamaResizer>
</template>
